import { useCallback } from "react";
import { cosmosApiUrl, isLocalCosmosApi, localCosmosHeaders } from "@/lib/cosmos-api-base-url";
import { useAihrAuth } from "@/lib/auth/useAihrAuth";

export function useCosmosApi() {
  const { authEnabled, getAccessToken } = useAihrAuth();

  return useCallback(
    async <T,>(
      path: string,
      options: RequestInit & { body?: BodyInit | null } = {}
    ): Promise<T> => {
      const token = authEnabled ? await getAccessToken() : undefined;
      const headers = new Headers(options.headers);
      headers.set("accept", "application/json");
      if (options.body && !headers.has("content-type")) {
        headers.set("content-type", "application/json");
      }
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      } else {
        if (!isLocalCosmosApi()) {
          throw new Error(
            "Auth0 is not configured for this Cosmos app environment."
          );
        }
        for (const [key, value] of Object.entries(localCosmosHeaders())) {
          headers.set(key, String(value));
        }
      }

      const response = await fetch(cosmosApiUrl(path), {
        ...options,
        headers
      });
      const text = await response.text();
      const data = text ? (JSON.parse(text) as T) : (undefined as T);
      if (!response.ok) {
        throw new Error(
          typeof data === "object" && data && "message" in data
            ? String((data as { message: unknown }).message)
            : `Cosmos request failed with ${response.status}`
        );
      }
      return data;
    },
    [authEnabled, getAccessToken]
  );
}
